/**
 * Cleanup Ledger rate estimator — the "honest estimator ladder" from
 * docs/cleanup-ledger-spec.md. Degrade, never fabricate: every input
 * shape returns SOME valid estimate, and "not enough data yet" is a
 * first-class state (Tier D), never an error.
 *
 * Pure functions, no DOM/Leaflet dependency — shared by both the
 * legacy Leaflet path and the MapLibre tile-mode path, same convention
 * as obs-popup.ts/region-count.ts.
 *
 * Tier ladder (spec):
 *   A — dated accumulation window (real dates, span >= 14 days): a
 *       rate BAND (never a point), +/-30%.
 *   B — >=2 dated observations of the same scene, span >= 14 days,
 *       same units (never mix item counts with 311 repeat counts):
 *       delta/weeks +/-40%.
 *   C — frequency-only (e.g. 311 repeat reports): qualitative word
 *       only (growing/holding/shrinking) via recent-half vs. prior-half
 *       comparison, no numeric band.
 *   D — single point or insufficient data: "not enough data yet" — a
 *       first-class state, not an error.
 */

export type EstimatorTier = 'A' | 'B' | 'C' | 'D';
export type TrendWord = 'growing' | 'holding' | 'shrinking' | 'not enough data yet';

/** A dated re-observation of the SAME scene (e.g. OLM photo_dates re-sweep). */
export interface DatedSceneObservation {
  /** ISO timestamp. Must be a REAL date (date_basis === 'photo_dates' or
   *  equivalent) — never a fabricated/ingestion-time stamp. */
  observed_at: string;
  /** Item count at this observation, or null if not counted. */
  items_estimate: number | null;
}

/** One logged cleanup_event against this scene. */
export interface DatedCleanupObservation {
  cleaned_at: string;
  items_estimate: number;
}

export interface EstimatorInput {
  /** All cleanup_events for this scene_observation_id, any order. */
  cleanupEvents: DatedCleanupObservation[];
  /** All dated re-observations of the SAME scene, any order. Empty if
   *  the scene has only ever been photographed once (or dates are
   *  fabricated/ingestion-time, which callers should exclude). */
  sceneObservations: DatedSceneObservation[];
  now?: Date; // injectable for tests
}

export interface RateEstimate {
  tier: EstimatorTier;
  /** Items per week, midpoint. Present only for tier A/B. */
  itemsPerWeekMidpoint?: number;
  /** Fractional band width, e.g. 0.3 for +/-30%. Present only for tier A/B. */
  bandFraction?: number;
  trendWord: TrendWord;
  /** Human copy for ledger render position (1). Null for tier C/D. */
  rateBandSentence: string | null;
}

const MS_PER_WEEK = 7 * 24 * 60 * 60 * 1000;
const MIN_WINDOW_DAYS = 14;
const TIER_A_BAND = 0.3;
const TIER_B_BAND = 0.4;
// Spec: "any estimate whose band exceeds ~50% of midpoint degrades to
// C/D wording." With the fixed 0.3/0.4 constants above this is
// currently unreachable, but the guard stays in place as a defensive
// contract in case a future confidence-interval-based band computation
// replaces the fixed percentages — the degrade behavior must never
// silently disappear.
const MAX_TRUSTED_BAND_FRACTION = 0.5;

function daysBetween(aIso: string, bIso: string): number {
  return Math.abs(new Date(bIso).getTime() - new Date(aIso).getTime()) / 86_400_000;
}

function weeksBetween(aIso: string, bIso: string): number {
  return Math.abs(new Date(bIso).getTime() - new Date(aIso).getTime()) / MS_PER_WEEK;
}

/** Never throws — insufficient/ambiguous input always resolves to Tier D. */
export function estimateRate(input: EstimatorInput): RateEstimate {
  try {
    return estimateRateInner(input);
  } catch {
    return tierD();
  }
}

function tierD(): RateEstimate {
  return { tier: 'D', trendWord: 'not enough data yet', rateBandSentence: null };
}

function tierCFromEvents(cleanupEvents: DatedCleanupObservation[]): RateEstimate {
  // Frequency-only trend: compare event count in the most recent half
  // of the observed history vs. the earlier half. Needs >=2 events to
  // say anything at all; otherwise there's no "trend" to report yet.
  if (cleanupEvents.length < 2) return tierD();
  const sorted = [...cleanupEvents].sort(
    (a, b) => new Date(a.cleaned_at).getTime() - new Date(b.cleaned_at).getTime(),
  );
  const mid = Math.floor(sorted.length / 2);
  const earlierHalf = sorted.slice(0, mid).length;
  const recentHalf = sorted.slice(mid).length;
  let trendWord: TrendWord;
  if (recentHalf > earlierHalf) trendWord = 'growing';
  else if (recentHalf < earlierHalf) trendWord = 'shrinking';
  else trendWord = 'holding';
  return { tier: 'C', trendWord, rateBandSentence: null };
}

function estimateRateInner(input: EstimatorInput): RateEstimate {
  const { sceneObservations, cleanupEvents } = input;

  // ── Tier A: a single dated accumulation window ──────────────────────
  // Requires >=2 dated scene observations with real item counts,
  // spanning >= MIN_WINDOW_DAYS, forming one continuous accumulation
  // (not scattered cleanup-event dates).
  const datedWithCounts = sceneObservations.filter((o) => o.items_estimate != null);
  if (datedWithCounts.length >= 2) {
    const sorted = [...datedWithCounts].sort(
      (a, b) => new Date(a.observed_at).getTime() - new Date(b.observed_at).getTime(),
    );
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const spanDays = daysBetween(first.observed_at, last.observed_at);
    if (spanDays >= MIN_WINDOW_DAYS) {
      const delta = (last.items_estimate as number) - (first.items_estimate as number);
      const weeks = weeksBetween(first.observed_at, last.observed_at);
      const itemsPerWeekMidpoint = weeks > 0 ? delta / weeks : 0;
      const result = withDegradeGuard('A', itemsPerWeekMidpoint, TIER_A_BAND);
      if (result) return result;
    }
  }

  // ── Tier B: >=2 dated observations, not a single clean accumulation ──
  // Combine scene observations and cleanup events (as "removal" points)
  // into one dated timeline; requires >=2 points spanning >= MIN_WINDOW_DAYS.
  const timelinePoints: Array<{ at: string; value: number }> = [
    ...datedWithCounts.map((o) => ({ at: o.observed_at, value: o.items_estimate as number })),
  ];
  if (timelinePoints.length < 2 && cleanupEvents.length >= 1 && datedWithCounts.length >= 1) {
    // A single dated scene observation plus at least one cleanup event
    // gives a second, later dated point (net accumulation is harder to
    // define across mixed sources, so Tier B here reports on frequency
    // only via tierC fallback below rather than fabricate a delta).
  }
  if (timelinePoints.length >= 2) {
    const sorted = [...timelinePoints].sort(
      (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
    );
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const spanDays = daysBetween(first.at, last.at);
    if (spanDays >= MIN_WINDOW_DAYS) {
      const delta = last.value - first.value;
      const weeks = weeksBetween(first.at, last.at);
      const itemsPerWeekMidpoint = weeks > 0 ? delta / weeks : 0;
      const result = withDegradeGuard('B', itemsPerWeekMidpoint, TIER_B_BAND);
      if (result) return result;
    }
  }

  // ── Tier C: frequency-only qualitative trend ─────────────────────────
  const tierC = tierCFromEvents(cleanupEvents);
  if (tierC.tier === 'C') return tierC;

  // ── Tier D: not enough data, ever ────────────────────────────────────
  return tierD();
}

function withDegradeGuard(
  tier: 'A' | 'B',
  itemsPerWeekMidpoint: number,
  bandFraction: number,
): RateEstimate | null {
  // Defensive guard (see MAX_TRUSTED_BAND_FRACTION comment) — currently
  // unreachable with fixed 0.3/0.4 constants, kept so a future
  // variance-based band computation can't silently violate the
  // never-fabricate-precision contract.
  if (bandFraction > MAX_TRUSTED_BAND_FRACTION) return null;

  const magnitude = Math.abs(itemsPerWeekMidpoint);
  const trendWord: TrendWord =
    magnitude < 0.5 ? 'holding' : itemsPerWeekMidpoint > 0 ? 'growing' : 'shrinking';
  const bandLow = Math.round(magnitude * (1 - bandFraction));
  const bandHigh = Math.round(magnitude * (1 + bandFraction));
  const rateBandSentence =
    trendWord === 'holding'
      ? 'holding steady — no clear trend yet'
      : `${trendWord} ~${bandLow}–${bandHigh} items/week`;
  return { tier, itemsPerWeekMidpoint, bandFraction, trendWord, rateBandSentence };
}

export interface GoalGradientInput {
  /** Current known items remaining at this scene, or null if unknown. */
  itemsRemainingEstimate: number | null;
  rate: RateEstimate;
}

export interface GoalGradient {
  /** "about five bags to done — at this pace, clear by Sept"; null when
   *  not honestly computable (e.g. tier C/D, or a growing/holding trend
   *  where "clear by" has no meaning). */
  sentence: string | null;
}

// Matches cleanup-buckets.ts's 'bag' midpoint. Exported for the
// zoom-ladder readout bar (readout-count.ts) so "≈ B bags" and the
// goal-gradient sentence can never disagree on bag size.
export const BAG_SIZE_ITEMS = 50;

export function estimateGoalGradient(input: GoalGradientInput): GoalGradient {
  const { itemsRemainingEstimate, rate } = input;
  if (itemsRemainingEstimate == null || itemsRemainingEstimate <= 0) {
    return { sentence: null };
  }
  // A goal-gradient ("clear by") only makes sense against a shrinking,
  // numerically-banded trend — growing/holding/qualitative-only/no-data
  // have no honest "done by" date.
  if (rate.tier !== 'A' && rate.tier !== 'B') return { sentence: null };
  if (rate.trendWord !== 'shrinking' || rate.itemsPerWeekMidpoint == null) {
    return { sentence: null };
  }
  const bagsToDone = Math.max(1, Math.round(itemsRemainingEstimate / BAG_SIZE_ITEMS));
  const weeksToDone = itemsRemainingEstimate / Math.abs(rate.itemsPerWeekMidpoint);
  if (!Number.isFinite(weeksToDone) || weeksToDone <= 0) return { sentence: null };
  const doneDate = new Date(Date.now() + weeksToDone * MS_PER_WEEK);
  const monthName = doneDate.toLocaleString('en-US', { month: 'long' });
  const bagsWord = bagsToDone === 1 ? 'bag' : 'bags';
  return {
    sentence: `about ${bagsToDone} ${bagsWord} to done — at this pace, clear by ${monthName}`,
  };
}

// ─── Bucket → items-estimate (display-only mirror) ──────────────────────
//
// The SERVER is the source of truth for items_estimate (never client-
// trusted — see cleanup-buckets.ts in dregsbane-web-backend). This is a
// local, display-only mirror so the client can render an optimistic
// ledger update immediately after logCleanupEvent() succeeds, before
// the server's authoritative response/next poll lands. Kept in sync by
// convention (same constants), not by a shared package — matches this
// codebase's existing "local-for-now, hoist later" pattern (see api.ts).
import type { AmountBucket } from './cleanup-types';

const BUCKET_MIDPOINT_ITEMS: Record<Exclude<AmountBucket, 'all'>, number> = {
  handful: 10,
  bag: 50,
  several_bags: 150,
};
const ALL_FALLBACK_ITEMS = 200;

export function bucketMidpointItems(
  bucket: AmountBucket,
  priorItemsEstimate?: number | null,
): number {
  if (bucket === 'all') {
    return priorItemsEstimate != null && priorItemsEstimate > 0
      ? priorItemsEstimate
      : ALL_FALLBACK_ITEMS;
  }
  return BUCKET_MIDPOINT_ITEMS[bucket];
}
